import React, { Component } from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import ToolsAvatar from "../clients/Tools/kit/ToolsAvatar";
import PlaybookRunService from "../../services/PlaybookRunService";
import PlaybookWaitingService from "../../services/PlaybookWaitingService";
import AgentRunOutcome from "./AgentRunOutcome";
import AgentFindingCounts from "./AgentFindingCounts";

import "./agent-run-table.css";

const SORTABLE_COLUMNS = {
	client: "client",
	period: "period",
	findings: "most_findings",
	started: "newest",
};

const MOST_NAMES_SHOWN = 2;

class AgentRunTable extends Component {
	onRowClicked = ({ run }) => {
		this.props.onOpenRun({ runId: run.id });
	};

	onRowKeyDown = ({ event, run }) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		this.onRowClicked({ run });
	};

	renderSortableHeader({ label, column, className }) {
		const { sort, onSort } = this.props;
		const sortKey = SORTABLE_COLUMNS[column];
		const isSorted = sort === sortKey;
		if (!onSort) return <span className={clsx("tk-table__head-cell", className)}>{label}</span>;

		return (
			<button
				type="button"
				className={clsx("tk-table__head-cell", "tk-table__sort", isSorted && "tk-table__sort--active", className)}
				onClick={() => onSort({ sort: sortKey })}
				aria-pressed={isSorted}
				title={isSorted ? `Sorted by ${label.toLowerCase()}, newest or largest first` : `Sort by ${label.toLowerCase()}`}
			>
				{label}
				{isSorted && <Icon.ArrowDown size={12} />}
			</button>
		);
	}

	renderHeader() {
		const { showClient, showAgent } = this.props;

		return (
			<div className={clsx("tk-table__row", "tk-table__row--head", "agent-run-table__row", this.columnsClass())}>
				{showClient && this.renderSortableHeader({ label: "Client", column: "client" })}
				{showAgent && <span className="tk-table__head-cell">Playbook</span>}
				{this.renderSortableHeader({ label: "Period", column: "period" })}
				<span className="tk-table__head-cell">Status</span>
				{this.renderSortableHeader({ label: "Findings", column: "findings" })}
				<span className="tk-table__head-cell">Waiting on</span>
				{this.renderSortableHeader({ label: "Started", column: "started", className: "tk-table__cell--end" })}
				<span className="tk-table__head-cell tk-table__cell--end" aria-hidden="true" />
			</div>
		);
	}

	renderWaitingOn({ run }) {
		if (!run.waitingOn) return <span className="tk-table__faint">—</span>;
		if (run.waitingOn.isUnassigned) {
			const { title, detail } = PlaybookWaitingService.describeWaitingOn({ run });
			return (
				<span className="agent-run-table__unassigned" title={detail ? `${title}. ${detail}` : title}>
					<Icon.AlertCircle size={13} />
					Unassigned
				</span>
			);
		}
		const people = run.waitingOn.people;
		const shownNames = people.slice(0, MOST_NAMES_SHOWN).join(", ");
		const moreCount = people.length - MOST_NAMES_SHOWN;
		return (
			<span className="tk-table__truncate" title={people.join(", ")}>
				{shownNames}
				{moreCount > 0 && <span className="tk-table__meta"> +{moreCount}</span>}
			</span>
		);
	}

	describeStarter({ run }) {
		if (run.run_trigger === "scheduled") return "Scheduled";
		return PlaybookRunService.shortNameFor({ user: run.started_by_user });
	}

	renderRow = run => {
		const { showClient, showAgent } = this.props;
		const clientName = run.client.name;
		const team = run.client.team ? run.client.team.name : "";
		const agentName = run.agent_version ? run.agent_version.name : "";

		return (
			<div
				key={run.id}
				role="button"
				tabIndex={0}
				className={clsx("tk-table__row", "tk-table__row--body", "tk-table__row--clickable", "agent-run-table__row", this.columnsClass())}
				onClick={() => this.onRowClicked({ run })}
				onKeyDown={event => this.onRowKeyDown({ event, run })}
			>
				{showClient && (
					<span className="tk-table__lead">
						<ToolsAvatar name={clientName} />
						<span className="tk-table__stack">
							<span className="tk-table__primary tk-table__truncate">{clientName}</span>
							{team && <span className="tk-table__meta tk-table__truncate">{team}</span>}
						</span>
					</span>
				)}
				{showAgent && <span className="tk-table__truncate">{agentName}</span>}
				<span className="tk-table__numeric">
					{PlaybookRunService.periodLabelFor({ periodEnd: run.period_end }) || <span className="tk-table__faint">—</span>}
				</span>
				<AgentRunOutcome run={run} />
				<AgentFindingCounts run={run} />
				{this.renderWaitingOn({ run })}
				<span className="tk-table__stack tk-table__cell--end">
					<span>{moment(run.created_at).fromNow()}</span>
					<span className="tk-table__meta">{this.describeStarter({ run })}</span>
				</span>
				<span className="tk-table__actions">
					<span className="tk-text-action tk-text-action--muted">
						Open
						<Icon.ChevronRight size={14} />
					</span>
				</span>
			</div>
		);
	};

	columnsClass() {
		const { showClient, showAgent } = this.props;
		return clsx(showClient && "agent-run-table__row--with-client", showAgent && "agent-run-table__row--with-agent");
	}

	render() {
		const { runs, isLoading, isRefreshing, emptyState } = this.props;

		if (isLoading) return <div className="tk-empty tk-card">Loading runs…</div>;
		if (!runs || runs.length === 0) return <div className="tk-empty tk-card">{emptyState}</div>;

		return (
			<div className={clsx("tk-table", "tk-card", isRefreshing && "tk-table--refreshing")} aria-busy={!!isRefreshing}>
				{this.renderHeader()}
				{runs.map(this.renderRow)}
			</div>
		);
	}
}

export default AgentRunTable;
